import type { DecisionDetail, Ordre } from '@hector/shared-types';

/**
 * Mise en forme du texte intégral d'une décision de justice — fonctions PURES,
 * testables sans rendu DOM. Consommé par `DecisionView` (le composant délègue
 * tout le découpage/typage de texte ici).
 *
 * Problème métier (incident 2026-06-18) : le `full_text` du corpus est
 * INCOHÉRENT d'une décision à l'autre. ~17 % des décisions longues sont des
 * « blobs » d'un seul tenant (0 retour à la ligne), le reste porte 40–230 `\n`.
 * Un simple `split(/\n+/)` rendait les blobs en un unique pavé illisible.
 */

/**
 * Frontières de paragraphe du droit français, détectées même SANS retour à la
 * ligne (lookahead `(?=…)` → le marqueur est conservé en tête du paragraphe
 * suivant). Couvre :
 *  - administratif classique : Vu / Considérant / Sur / Par un… / numéros `1°)` ;
 *  - administratif moderne (post-2019) : numérotation décimale `3. ` + sous-titres
 *    « En ce qui concerne » / « S'agissant » / « En premier lieu » ;
 *  - judiciaire : Attendu / PAR CES MOTIFS / REJETTE / CASSE…
 *
 * WHY ancré sur des ouvreurs à majuscule + numéros : éviter de couper en plein
 * milieu d'une phrase (« par cet établissement » minuscule ne matche pas `Par `).
 * WHY la numérotation décimale `\d{1,2}\.\s` est ancrée par lookbehind
 * `(?<=[.;:]\s)` : sans cette garde, la fin d'une année ou d'un montant
 * (« …novembre 2018. ») déclencherait un faux découpage. Le point n° ne compte
 * que s'il suit immédiatement une ponctuation de fin de phrase.
 */
const PARA_BOUNDARY =
  /(?=(?:\d{1,2}°\s*\)|[IVX]{1,3}°\s*\)|Vu\b|Sur\b|Considérant|Attendu\b|Mais attendu|Sans qu'il|En ce qui concerne|S'agissant|En (?:premier|deuxième|second|troisième) lieu|Par ces motifs|PAR CES MOTIFS|D[ÉE]CIDE\b|ORDONNE\b|REJETTE\b|CASSE\b|CONDAMNE\b|D[ÉE]CLARE\b|Article\s|ARTICLE\s|Par (?:un|une|des)\b|Après avoir|Elle soutient|Ils soutiennent|Elles soutiennent|Il soutient|La parole))|(?<=[.;:]\s)(?=\d{1,2}\.\s)/g;

/**
 * Fragment réduit à un seul marqueur numéroté (« I°) », « 1°) ») sans contenu —
 * survient quand le marqueur suivant le suit immédiatement (« Vu : I°) Par une
 * requête… »). On le fusionne avec le paragraphe suivant pour éviter une ligne
 * orpheline de 3 caractères.
 */
const MARKER_ONLY = /^(?:\d{1,2}\s*°\s*\)|[IVX]{1,3}\s*°\s*\))$/;

/**
 * Au-delà de cette longueur, un bloc est considéré comme un « blob » potentiel
 * et sous-découpé aux frontières structurelles. En dessous, on le laisse tel
 * quel (un paragraphe déjà séparé par `\n` n'a pas besoin d'être re-coupé).
 */
const BLOB_THRESHOLD = 400;

/**
 * Découpe le texte intégral en paragraphes lisibles, quelle que soit la qualité
 * de formatage source.
 *
 * 1. Découpe grossière sur les `\n` existants (respecte le formatage propre).
 * 2. Tout bloc encore long (blob sans `\n`, ou longue narration procédurale)
 *    est re-découpé aux frontières `PARA_BOUNDARY`.
 *
 * @param fullText - `decision.fullText` (peut être null/vide)
 * @returns liste de paragraphes non vides, trimés
 */
export function splitDecisionIntoParagraphs(fullText: string | null | undefined): string[] {
  const coarse = (fullText ?? '')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const raw = coarse.flatMap((block) =>
    block.length > BLOB_THRESHOLD
      ? block.split(PARA_BOUNDARY).map((s) => s.trim()).filter(Boolean)
      : [block],
  );
  // Fusionne les marqueurs orphelins (« I°) » seul) avec le paragraphe suivant.
  const merged: string[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const current = raw[i] as string;
    const next = raw[i + 1];
    if (MARKER_ONLY.test(current) && next !== undefined) {
      merged.push(`${current} ${next}`);
      i += 1;
    } else {
      merged.push(current);
    }
  }
  return merged;
}

/**
 * Vrai si le paragraphe ouvre sur un marqueur canonique → stylé en titre de
 * section (gras) par `DecisionView`. Sous-ensemble des structures fortes (on
 * ne met PAS en gras chaque numéro « 1°) » qui reste du corps de texte).
 *
 * WHY sans flag `g` : un regex stateful partagerait son `lastIndex` entre les
 * `.test()` du `.map()` de rendu → un test sur deux raté de façon intermittente.
 */
const HEADING_RE =
  /^(Vu\b|Sur\b|Considérant\b|Attendu\b|Mais attendu|Sans qu'il soit|D[ÉE]CIDE\b|ORDONNE\b|REJETTE\b|CASSE\b|Article\b|ARTICLE\b|Par ces motifs|PAR CES MOTIFS|Procédure)/;

export function isHeadingParagraph(paragraph: string): boolean {
  return HEADING_RE.test(paragraph);
}

/** Libellés complets des juridictions, indexés par code (court/jurisdiction lowercasé). */
const JURIDICTION_LABELS: Record<string, string> = {
  ce: "Conseil d'État",
  caa: "Cour administrative d'appel",
  ta: 'Tribunal administratif',
  cc: 'Cour de cassation',
  cass: 'Cour de cassation',
  ca: "Cour d'appel",
  tj: 'Tribunal judiciaire',
  tgi: 'Tribunal de grande instance',
  tcom: 'Tribunal de commerce',
  tc: 'Tribunal de commerce',
  cph: "Conseil de prud'hommes",
};

/**
 * Libellé complet de la juridiction pour l'en-tête « titre ».
 *
 * WHY le garde-fou `ordre` : `court` peut être un code inconnu ; on retombe
 * alors sur un générique cohérent avec l'ordre plutôt que d'afficher un code
 * cryptique. (Le piège `court='cc'` défauté ne concerne PAS le chemin viewer —
 * `fetchDecisionForViewer` lit `jurisdiction` brut pour l'administratif.)
 */
export function formatJuridictionLabel(court: string | null | undefined, ordre: Ordre): string {
  const key = court?.trim().toLowerCase();
  if (key && JURIDICTION_LABELS[key]) return JURIDICTION_LABELS[key];
  if (key) return court!.trim();
  return ordre === 'administratif' ? 'Juridiction administrative' : 'Décision';
}

/** Élision « d'Aix » vs « de Paris » selon l'initiale (voyelle/h → d'). */
function prefixDeOrD(city: string): string {
  const first = city.charAt(0).toLowerCase();
  return 'aeiouhàâéèêîïôöûü'.includes(first) ? "d'" : 'de ';
}

/**
 * Met en forme un slug de ville canonique (« aix_en_provence », « la_rochelle »)
 * en libellé lisible (« Aix-en-Provence », « La Rochelle »). Les mots de liaison
 * restent en minuscules sauf en tête.
 */
function formatCity(slug: string): string {
  const liaisons = new Set(['en', 'de', 'la', 'le', 'sur', 'sous', 'du', 'des', 'les', 'aux', 'au', 'lès']);
  return slug
    .trim()
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part, i) => (i > 0 && liaisons.has(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('-');
}

const COURT_NAME_RE = /^(tribunal|cour|conseil|chambre)/i;

/**
 * Titre « nom de la juridiction » de l'en-tête viewer, ENRICHI de la ville.
 *
 * - **administratif** : `location` = `juridiction_nom`. Pour TA/CAA c'est le nom officiel
 *   COMPLET avec ville (« Tribunal Administratif de Toulon ») → on le renvoie verbatim.
 *   Pour le CE, `juridiction_nom` vaut une formation (« Section du Contentieux ») et ne
 *   nomme pas une cour → on retombe sur le libellé du code (« Conseil d'État », national).
 * - **judiciaire** : `location` = `location_canonical` (slug ville). On compose
 *   « {libellé} {de|d'}{Ville} » (« Cour d'appel de Paris », « Tribunal de commerce d'Aubenas »).
 *   Sans ville (CC national, ou slug absent) → libellé seul.
 */
export function formatDecisionTitle(
  decision: Pick<DecisionDetail, 'court' | 'ordre' | 'location'>,
): string {
  const base = formatJuridictionLabel(decision.court, decision.ordre);
  const loc = decision.location?.trim();
  if (!loc) return base;

  if (decision.ordre === 'administratif') {
    // juridiction_nom : nom officiel complet (TA/CAA) → verbatim ; sinon (CE : formation) → libellé.
    return COURT_NAME_RE.test(loc) ? loc : base;
  }

  // judiciaire : libellé + ville canonique. Si `loc` nomme déjà une cour, le prendre verbatim.
  if (COURT_NAME_RE.test(loc)) return loc;
  const city = formatCity(loc);
  return city ? `${base} ${prefixDeOrD(city)}${city}` : base;
}

const MOIS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/**
 * Formate une date ISO (`2022-10-06`) en français long (`6 octobre 2022`).
 * Retourne la valeur brute si elle n'est pas au format ISO attendu, `null` si absente.
 */
export function formatDecisionDateFr(date: string | null | undefined): string | null {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  const [, year, month, day] = m;
  const monthLabel = MOIS_FR[Number(month) - 1];
  return monthLabel ? `${Number(day)} ${monthLabel} ${year}` : date;
}

/**
 * Ligne de citation « nom de la JP » sous le titre : date longue + n° de pourvoi/dossier.
 * Ex. `6 octobre 2022 · n° 446764`.
 */
export function formatDecisionCitation(decision: Pick<DecisionDetail, 'date' | 'numero'>): string {
  return [formatDecisionDateFr(decision.date), decision.numero ? `n° ${decision.numero}` : null]
    .filter(Boolean)
    .join(' · ');
}
